import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { isCreditNote, isDebitNote } from "@fiscalo/shared-types";

export interface ReissueRejectedInvoiceVariables {
  invoiceId: string;
  /** CFE type of the rejected invoice. When it's a credit/debit note, navigates to the note wizard instead. */
  cfeType?: string;
  /** For credit/debit notes: the referenced original invoice ID. */
  referencedInvoiceId?: string;
}

export function useReissueRejectedInvoice(): {
  reissue: (vars: ReissueRejectedInvoiceVariables) => void;
  isPending: boolean;
} {
  const navigate = useNavigate();

  const reissue = useCallback(
    ({ invoiceId, cfeType, referencedInvoiceId }: ReissueRejectedInvoiceVariables) => {
      if (cfeType && isCreditNote(cfeType) && referencedInvoiceId) {
        navigate(`/invoices/${referencedInvoiceId}/credit-note`);
      } else if (cfeType && isDebitNote(cfeType) && referencedInvoiceId) {
        navigate(`/invoices/${referencedInvoiceId}/debit-note`);
      } else {
        // Put the source id in the URL — survives reload, deep links,
        // and "open in new tab" on the row's row-action menu. The
        // page reads it back via useSearchParams. State payload still
        // travels alongside as a hint so the page can render the
        // reissue helper copy on the first paint, before the
        // background invoice fetch resolves.
        navigate(
          `/invoices/new?reissueFromId=${encodeURIComponent(invoiceId)}`,
          { state: { reissueFromId: invoiceId } },
        );
      }
    },
    [navigate],
  );

  return {
    reissue,
    isPending: false,
  };
}
